import React from 'react';
import { useDataFetching } from '../../hooks/useDataFetching';
import LoadingState from '../shared/LoadingState';
import ErrorState from '../shared/ErrorState';
import StatCard from '../shared/StatCard';
import { formatRelativeTime } from '../../utils/formatters';
import { AlertTriangle } from 'lucide-react';

const Anomalies = () => {
  const { data: anomalies, loading, error, refetch } = useDataFetching(
    async () => {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/anomalies`);
      if (!response.ok) throw new Error('Failed to fetch anomalies');
      return response.json();
    },
    []
  );

  if (loading) return <LoadingState message="Loading anomalies..." />;
  if (error) return <ErrorState message={error} onRetry={refetch} />;

  const activeAnomalies = anomalies?.filter(a => a.status === 'active').length || 0;
  const criticalAnomalies = anomalies?.filter(a => a.severity === 'Critical').length || 0;
  const avgConfidence = anomalies?.length
    ? (anomalies.reduce((sum, a) => sum + a.confidence_score, 0) / anomalies.length * 100).toFixed(1)
    : 0;

  const getSeverityColor = (severity) => {
    if (severity === 'Critical') return 'bg-red-100 text-red-800 border-red-200';
    if (severity === 'High') return 'bg-orange-100 text-orange-800 border-orange-200';
    if (severity === 'Medium') return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-blue-100 text-blue-800 border-blue-200';
  };

  const getStatusColor = (status) => {
    if (status === 'resolved') return 'bg-green-100 text-green-800';
    if (status === 'active') return 'bg-red-100 text-red-800';
    if (status === 'investigating') return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Detected Anomalies</h1>
          <p className="mt-2 text-gray-600">
            Monitor and investigate anomalies detected across your infrastructure
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Anomalies"
            value={anomalies?.length || 0}
            subtitle="Detected"
          />
          <StatCard
            title="Active Anomalies"
            value={activeAnomalies}
            subtitle="Requiring attention"
          />
          <StatCard
            title="Critical"
            value={criticalAnomalies}
            subtitle="High severity"
          />
          <StatCard
            title="Avg Confidence"
            value={`${avgConfidence}%`}
            subtitle="Detection accuracy"
          />
        </div>

        {/* Anomalies List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Recent Anomalies</h2>
            <p className="text-sm text-gray-600 mt-1">AI-detected anomalies across monitored services</p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Anomaly Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Severity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Confidence
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Detected
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {anomalies && anomalies.map((anomaly) => (
                  <tr key={anomaly.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <AlertTriangle className="h-5 w-5 text-orange-500 mr-2" />
                        <div className="text-sm font-medium text-gray-900">{anomaly.service_name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{anomaly.anomaly_type}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-md">{anomaly.description}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        Affected: {anomaly.affected_metrics.join(', ')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getSeverityColor(anomaly.severity)}`}>
                        {anomaly.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(anomaly.status)}`}>
                        {anomaly.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{(anomaly.confidence_score * 100).toFixed(0)}%</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatRelativeTime(anomaly.detected_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button className="text-blue-600 hover:text-blue-900">
                        Investigate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Anomalies;